import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Card, Steps, Checkbox, Row, Col } from "antd";
import {
  LockOutlined,
  UserOutlined,
  MailOutlined,
  BankOutlined,
  LoadingOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  SolutionOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import BGImage from "../../assets/images/SAMPLE1.jpg";
import { toast } from "react-toastify";
import { getData, postData } from "../../api/apiService";
import { FaPhoneAlt } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import SignupSuccessModal from "./SignupSuccessModal";
import Logo from "../../assets/images/NabsLogoOld.png";
import NoticeAILogo from "../../assets/images/LoadingLogo.png";
import AuthFeaturesSection from "../../components/AuthFeaturesSection";
import { AUTH } from "../../api/endpointMap";

const { Step } = Steps;

const Signup = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [form] = Form.useForm();
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const { checkReadOnlyMode, checkIsFirstPurchase } = useAuth();
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const otpRefs = Array(6)
    .fill(0)
    .map(() => React.createRef());

  useEffect(() => {
    checkReadOnlyMode();
    // checkIsFirstPurchase();
  }, []);

  const handleUserInfoSubmit = async (values) => {
    try {
      setLoading(true);

      setFormData(values);

      // Pass email so backend can run the same email+phone duplicate check
      // it already runs post-OTP in signup(). Resend OTP intentionally omits
      // email and skips the gate.
      const otpPayload = {
        phone_number: values.phone_number,
        email: values.email,
      };

      const otpResponse = await postData(
        AUTH.SEND_OTP,
        otpPayload
      );

      setLoading(false);

      if (otpResponse) {
        toast.info("OTP sent successfully! Please verify your account.");
        setCurrentStep(1);
        setResendTimer(60);
        setCanResend(false);
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      toast.error(err || "Failed to send OTP");
      setError(err || "Failed to send OTP");
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpLoading(true);

      const otp = otpValues.join("");

      if (otp.length !== 6) {
        toast.error("Please enter a valid 6-digit OTP");
        setOtpLoading(false);
        return;
      }

      const verifyPayload = {
        phone_number: formData.phone_number,
        otp_code: otp,
      };

      const verifyResponse = await postData(
        AUTH.VERIFY_OTP,
        verifyPayload
      );

      if (verifyResponse) {
        toast.info("OTP verified successfully!");

        const signupPayload = {
          ...formData,
          password: formData.password_1,
          state: "Haryana",
          referred: "Website",
        };

        const utm_payload = {
          first_name: formData.first_name,
          middle_name: "",
          last_name: formData.last_name,
          organization: formData.company_name,
          email: formData.email,
          mobile_no: formData.phone_number,
          source: "Website",
          custom_crm_subsource_: "Portal Sign Up",
          status: "New",
          custom_crm_source_reference: "",
        };

        const response = await postData(
          AUTH.SIGN_UP,
          signupPayload
        );

        // FE-side direct CRM Lead call DISABLED — was creating duplicate
        // leads on production (office.nabsai.com) even during local testing.
        // BE-side crm_sync.event_trial_signup (users.py:469) already creates
        // the lead in whichever CRM the backend .env CRM_SYNC_URL points to
        // (local for dev, production for live). Single source of truth.
        // To re-enable FE-side UTM attribution later, move this call to the
        // backend (so the API token isn't shipped in the client bundle).
        // try {
        //   await axios.post(
        //     "https://office.nabsai.com/api/resource/CRM Lead",
        //     utm_payload,
        //     {
        //       headers: {
        //         "Content-Type": "application/json",
        //         Authorization: "token 64e58fa2338a256:dd45cc96e152968",
        //       },
        //     }
        //   );
        // } catch (utmErr) {
        //   console.error("UTM CRM call failed:", utmErr);
        // }

        toast.info("Signed up successfully, Please Login!");
        navigate("/login");
        // setIsOpen(true);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }

      setOtpLoading(false);
    } catch (err) {
      setOtpLoading(false);
      toast.error(err || "Failed to verify OTP");
      setError(err || "Failed to verify OTP");
    }
  };

  const resendOtp = async () => {
    try {
      setLoading(true);

      const otpPayload = {
        phone_number: formData.phone_number,
      };

      const otpResponse = await postData(
        AUTH.SEND_OTP,
        otpPayload
      );

      setLoading(false);

      if (otpResponse) {
        toast.info("OTP resent successfully!");
        setResendTimer(60);
        setCanResend(false);
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      toast.error(err || "Failed to resend OTP");
    }
  };

  useEffect(() => {
    let timer;
    if (!canResend && resendTimer > 0) {
      timer = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, canResend]);

  const renderUserInfoForm = () => (
    <Form
      name="signup-form"
      form={form}
      onFinish={handleUserInfoSubmit}
      layout="vertical"
    >
      <div
        style={{
          display: "flex",
          marginBottom: "-5px",
          gap: window.innerWidth <= 768 ? "10px" : "10px",
          flexDirection: window.innerWidth <= 768 ? "row" : "row",
        }}
      >
        <Form.Item
          name="first_name"
          label="First Name"
          rules={[
            {
              required: true,
              message: "Please enter your first name!",
            },
          ]}
          style={{ flex: 1 }}
        >
          <Input prefix={<UserOutlined />} placeholder="First Name" maxLength={140} />
        </Form.Item>

        <Form.Item
          name="last_name"
          label="Last Name"
          rules={[{ required: true, message: "Please enter your last name!" }]}
          style={{ flex: 1 }}
        >
          <Input prefix={<UserOutlined />} placeholder="Last Name" maxLength={140} />
        </Form.Item>
      </div>
      <div
        style={{
          display: "flex",
          gap: window.innerWidth <= 768 ? "10px" : "10px",
          flexDirection: window.innerWidth <= 768 ? "row" : "row",
        }}
      >
        <Form.Item
          name="company_name"
          label="Company Name"
          style={{ flex: 1 }}
          rules={[
            {
              required: true,
              message: "Please enter your company name!",
            },
          ]}
        >
          <Input prefix={<BankOutlined />} placeholder="Company Name" maxLength={140} />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email ID"
          style={{ flex: 1 }}
          rules={[
            { required: true, message: "Please enter your email!" },
            { type: "email", message: "Please enter a valid email!" },
          ]}
        >
          <Input prefix={<MailOutlined />} placeholder="Email ID" />
        </Form.Item>
      </div>
      <div
        style={{
          display: "flex",
          gap: window.innerWidth <= 768 ? "10px" : "10px",
          flexDirection: window.innerWidth <= 768 ? "row" : "row",
        }}
      >
        <Form.Item
          name="phone_number"
          label="Phone Number"
          rules={[
            {
              required: true,
              message: "Please enter your phone number!",
            },
            {
              pattern: /^[6-9]\d{9}$/,
              message: "Please enter a valid 10-digit mobile number!",
            },
            {
              len: 10,
              message: "Phone number must be exactly 10 digits!",
            },
          ]}
          style={{ flex: 1 }}
        >
          <Input
            prefix={<FaPhoneAlt />}
            placeholder="10-digit phone number"
            maxLength={10}
            type="tel"
          />
        </Form.Item>
        <Form.Item name="message" style={{ flex: 1 }} label="Message">
          <Input prefix={<MessageOutlined />} placeholder="Message" />
        </Form.Item>
      </div>
      <div
        style={{
          display: "flex",
          gap: window.innerWidth <= 768 ? "0px" : "10px",
          flexDirection: window.innerWidth <= 768 ? "column" : "row",
        }}
      >
        <Form.Item
          name="password_1"
          label="Password"
          rules={[
            { required: true, message: "Please enter your password!" },
            { min: 8, message: "Password must be at least 8 characters!" },
          ]}
          style={{ flex: 1 }}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="Password" maxLength={128} />
        </Form.Item>

        <Form.Item
          name="password_2"
          label="Confirm Password"
          rules={[
            {
              required: true,
              message: "Please confirm your password!",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password_1") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The two passwords do not match!")
                );
              },
            }),
          ]}
          style={{ flex: 1 }}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm Password"
          />
        </Form.Item>
      </div>

      <Form.Item>
        <Checkbox
          checked={agreeToTerms}
          onChange={(e) => setAgreeToTerms(e.target.checked)}
        >
          I have read and agree to the{" "}
          <a
            href="/terms-and-conditions"
            style={{
              color: "#004080",
              cursor: "pointer",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            Terms and Conditions
          </a>
        </Checkbox>
        <br />
        <span
          style={{
            textAlign: "center",
            marginTop: "15px",
            color: "#666",
          }}
        >
          By clicking "Continue" or by using the NoticeAI, you acknowledge that
          you have read, understood, and agree to be bound by these Terms and
          Conditions.
        </span>
      </Form.Item>

      <Form.Item>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            type="primary"
            htmlType="submit"
            block
            style={{ backgroundColor: "#004080" }}
            disabled={!agreeToTerms || loading}
          >
            {loading ? <LoadingOutlined spin /> : "Continue"}
          </Button>
        </motion.div>
      </Form.Item>
    </Form>
  );

  const renderOtpForm = () => (
    <div style={{ padding: "20px 10px" }}>
      <h2
        style={{ textAlign: "center", marginBottom: "20px", color: "#004080" }}
      >
        OTP Verification
      </h2>
      <p style={{ textAlign: "center", marginBottom: "20px" }}>
        A verification code has been sent to your phone number. Please enter the
        6-digit code below.
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "30px",
        }}
      >
        {otpValues.map((value, index) => (
          <Input
            key={index}
            ref={otpRefs[index]}
            value={value}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            maxLength={1}
            style={{
              width: "45px",
              height: "45px",
              margin: "0 5px",
              textAlign: "center",
              fontSize: "18px",
              borderRadius: "8px",
            }}
          />
        ))}
      </div>

      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{ marginBottom: "15px" }}
      >
        <Button
          type="primary"
          block
          style={{ backgroundColor: "#004080" }}
          onClick={handleVerifyOtp}
          disabled={otpValues.some((v) => !v) || otpLoading}
        >
          {otpLoading ? <LoadingOutlined spin /> : "Verify OTP"}
        </Button>
      </motion.div>

      <div style={{ textAlign: "center" }}>
        <Button
          type="link"
          onClick={resendOtp}
          disabled={!canResend || loading}
        >
          {loading ? (
            <LoadingOutlined spin />
          ) : canResend ? (
            "Resend OTP"
          ) : (
            `Resend in ${resendTimer}s`
          )}
        </Button>

        <Button
          type="link"
          onClick={() => {
            setCurrentStep(0);
            setOtpValues(["", "", "", "", "", ""]);
          }}
        >
          Change Details
        </Button>
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        flexDirection: "row",
        backgroundColor: "#f0f5ff",
        position: "relative",
      }}
    >
      <div
        style={{
          flex: 0.4,

          display: window.innerWidth > 768 ? "block" : "none",
        }}
      >
        <AuthFeaturesSection />
      </div>

      <div
        style={{
          flex: window.innerWidth > 768 ? 0.6 : 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          position: "relative",
          background:
            window.innerWidth <= 768
              ? `url(${BGImage}) center/cover no-repeat`
              : "#f0f5ff",
        }}
      >
        {/* Background Overlay on Mobile */}
        {window.innerWidth <= 768 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 1,
            }}
          ></div>
        )}

        {/* Navigation Buttons */}
        {window.innerWidth >= 768 && (
          <div style={{ position: "absolute", top: 20, right: 20, zIndex: 2 }}>
            <Button
              type="link"
              style={{ fontWeight: "600", textDecoration: "underline" }}
              onClick={() => navigate("/login")}
            >
              <UserOutlined /> Login
            </Button>
          </div>
        )}

        {/* Animated Heading */}
        {window.innerWidth >= 768 && (
          <motion.img
            src={NoticeAILogo}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="noticeAIAUTHLogo"
            style={{
              marginBottom: 20,
              zIndex: 2,
              width: "150px",
            }}
          />
        )}

        {/* Steps */}
        {/* <div
          style={{
            width: window.innerWidth <= 768 ? "100%" : "750px",
            marginBottom: "20px",
            zIndex: 2,
          }}
        >
          <Steps current={currentStep} style={{ padding: "0 20px" }}>
            <Step title="Register" icon={<UserSwitchOutlined />} />
            <Step title="Verify" icon={<SolutionOutlined />} />
            <Step title="Done" icon={<CheckCircleOutlined />} />
          </Steps>
        </div> */}

        {/* Animated Signup Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            width: window.innerWidth <= 768 ? "100%" : "750px",
            zIndex: 2,
            overflow: window.innerWidth <= 768 ? "auto" : "hidden",
          }}
        >
          <Card
            style={{
              width: "100%",
              padding: "0px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            {currentStep === 0 && renderUserInfoForm()}
            {currentStep === 1 && renderOtpForm()}
          </Card>
        </motion.div>
      </div>

      {isOpen && (
        <SignupSuccessModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          email={formData.email}
          password={formData.password_1 || formData.password_2}
        />
      )}
    </div>
  );
};

export default Signup;
